import { Reason } from "../types/reason";
import { baseApi, QueryResponse } from "./base";

const TAG_TYPE: string = "reasons";
const apiWithTag = baseApi.enhanceEndpoints({ addTagTypes: [TAG_TYPE] });

export const reasonApi = apiWithTag.injectEndpoints({
  endpoints: (builder) => ({
    getReasons: builder.query<
      QueryResponse<Reason>,
      {
        page: number;
        pageSize: number;
        sortField?: string;
        sortMode?: string | null;
        search?: string;
        withTrashed?: boolean;
      }
    >({
      query: ({
        page,
        pageSize,
        sortField,
        sortMode,
        search,
        withTrashed = true,
      }) => ({
        url: "api/admin/reasons",
        method: "GET",
        params: {
          per_page: pageSize,
          page: page + 1,
          sort:
            !!sortField && !!sortMode ? `${sortField}|${sortMode}` : `name|asc`,
          withTrashed,
          search,
        },
      }),
      providesTags: (result) => {
        const data = result?.data;
        if (!data) return [{ type: TAG_TYPE, id: "PARTIAL-LIST" }];
        return [
          { type: TAG_TYPE, id: "PARTIAL-LIST" },
          ...data?.map(({ id }) => ({ type: TAG_TYPE, id })),
        ];
      },
    }),
    getAllReasons: builder.query<
      Array<Reason>,
      { sortField?: string; sortMode?: string | null; search?: string }
    >({
      query: ({ sortField, sortMode, search }) => ({
        url: "api/admin/reasons",
        method: "GET",
        params: {
          sort:
            !!sortField && !!sortMode ? `${sortField}|${sortMode}` : `name|asc`,
          search,
        },
      }),
      providesTags: (result) => {
        const data = result;
        if (!data) return [{ type: TAG_TYPE, id: "PARTIAL-LIST", mode: "ALL" }];
        return [
          { type: TAG_TYPE, id: "PARTIAL-LIST", mode: "ALL" },
          ...data?.map(({ id }) => ({ type: TAG_TYPE, id })),
        ];
      },
    }),
    getReasonById: builder.query<Reason, number>({
      query: (id) => ({
        url: `api/admin/reasons/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: TAG_TYPE, id }],
    }),
    addReason: builder.mutation<
      Reason & { skills: number[] },
      Omit<Reason, "id">
    >({
      query: (body) => ({
        url: `api/admin/reasons`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, id) => [
        { type: TAG_TYPE, id: "PARTIAL-LIST" },
      ],
    }),
    editReason: builder.mutation<
      Reason,
      Partial<Reason & { skills: number[] }> & Pick<Reason, "id">
    >({
      query: (body) => ({
        url: `api/admin/reasons/${body.id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, data) => [
        { type: TAG_TYPE, id: data.id },
        { type: TAG_TYPE, id: "PARTIAL-LIST" },
        { type: "VerticalSkills", id: data.id },
      ],
    }),
    deleteReason: builder.mutation<void, number>({
      query: (id) => ({
        url: `api/admin/reasons/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: TAG_TYPE, id },
        { type: TAG_TYPE, id: "PARTIAL-LIST" },
      ],
    }),
    restoreReason: builder.mutation<void, number>({
      query: (id) => ({
        url: `api/admin/reasons/${id}/restore`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: TAG_TYPE, id },
        { type: TAG_TYPE, id: "PARTIAL-LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetReasonsQuery,
  useGetAllReasonsQuery,
  useGetReasonByIdQuery,
  useAddReasonMutation,
  useEditReasonMutation,
  useDeleteReasonMutation,
  useRestoreReasonMutation,
} = reasonApi;
